/**
 * The "pixel strip": data hidden in extra rows at the bottom of an image,
 * one byte per pixel, using kinklist-v2's 3-3-2 scheme (SPEC §1.3, §7.3):
 *
 *   r = 254 - (byte & 7), g = 254 - ((byte >> 3) & 7), b = 254 - (byte >> 6)
 *
 * Bytes start at the bottom-left pixel and run left→right, then up a row.
 * A pure white pixel terminates the data.
 */
import { latin1Decode, utf8Decode } from './bytes';

export interface RgbaImage {
  width: number;
  height: number;
  /** Row-major RGBA, 4 bytes per pixel. */
  data: Uint8ClampedArray | Uint8Array;
}

/**
 * Encodes `bytes` as strip rows for an image `width` pixels wide. The result
 * is meant to be drawn at the very bottom of the image. It always contains at
 * least one white terminator pixel (v2 relied on the white margin above).
 */
export function encodePixelStrip(bytes: Uint8Array, width: number): RgbaImage {
  if (!Number.isInteger(width) || width < 1) throw new RangeError('width must be ≥ 1');
  const height = Math.floor(bytes.length / width) + 1;
  const data = new Uint8ClampedArray(width * height * 4).fill(255);
  bytes.forEach((byte, i) => {
    const row = height - 1 - Math.floor(i / width);
    const offset = (row * width + (i % width)) * 4;
    data[offset] = 254 - (byte & 0b111);
    data[offset + 1] = 254 - ((byte >> 3) & 0b111);
    data[offset + 2] = 254 - (byte >> 6);
  });
  return { width, height, data };
}

/**
 * Reads strip bytes from the bottom of `image`. Returns `null` when there is
 * no data (first pixel white), a pixel isn't a valid data pixel, or no
 * terminator is found before the top of the image.
 */
export function decodePixelStrip(image: RgbaImage): Uint8Array | null {
  const { width, height, data } = image;
  const bytes: number[] = [];
  for (let y = height - 1; y >= 0; y--) {
    for (let x = 0; x < width; x++) {
      const offset = (y * width + x) * 4;
      const r = data[offset] ?? 0;
      const g = data[offset + 1] ?? 0;
      const b = data[offset + 2] ?? 0;
      if (r === 255 && g === 255 && b === 255) {
        return bytes.length === 0 ? null : Uint8Array.from(bytes);
      }
      const lo = 254 - r;
      const mid = 254 - g;
      const hi = 254 - b;
      if (lo < 0 || lo > 7 || mid < 0 || mid > 7 || hi < 0 || hi > 3) return null;
      bytes.push(lo | (mid << 3) | (hi << 6));
    }
  }
  return null;
}

/** v3 strips hold UTF-8; v2 strips hold one (truncated) byte per character. */
export function decodeStripText(bytes: Uint8Array): string {
  try {
    return utf8Decode(bytes);
  } catch {
    return latin1Decode(bytes);
  }
}
